export const commerceProviders = ["stripe"];
export const stripeConnectAccountArchitectures = ["standard"];
export const commerceReadinessStates = [
    "notConnected",
    "onboardingRequired",
    "pending",
    "restricted",
    "ready",
];
export const commerceOnboardingStatuses = [
    "notStarted",
    "started",
    "detailsSubmitted",
    "restricted",
    "ready",
];
export const commerceCapabilityStatuses = ["active", "inactive", "pending", "unknown"];
export const donationTransactionStatuses = [
    "pending",
    "requiresAction",
    "processing",
    "confirmed",
    "failed",
    "cancelled",
];
export const donationRefundStatuses = [
    "pending",
    "processing",
    "confirmed",
    "failed",
    "cancelled",
];
export const donationRefundSummaryStatuses = [
    "none",
    "processing",
    "partiallyRefunded",
    "refunded",
    "failed",
];
export const donationReceiptTypes = [
    "donation",
    "refund",
    "iftarSponsorship",
    "spaceReservation",
];
export const financialLedgerEntryTypes = [
    "donation_organization_net",
    "donation_refund_organization_net",
    "iftar_sponsorship_organization_net",
    "space_reservation_organization_net",
];
export const financialLedgerDirections = ["credit", "debit"];
export const givingFundTypes = ["general", "zakat", "sadaqah", "custom"];
export const givingFundStatuses = ["draft", "active", "archived"];
export const givingFundVisibilities = ["connected", "private"];
export const campaignStatuses = ["draft", "published", "completed", "archived"];
export const campaignVisibilities = ["connected", "private"];
export const donationDestinationTypes = ["general", "fund", "campaign"];
export const commerceReconciliationStatuses = [
    "matched",
    "mismatch",
    "providerMissing",
    "shdeedMissing",
    "stale",
    "error",
];
export const commerceReconciliationMismatchTypes = [
    "provider_payment_intent_missing",
    "provider_payment_intent_status_mismatch",
    "provider_amount_mismatch",
    "provider_currency_mismatch",
    "refund_amount_mismatch",
    "refund_status_mismatch",
    "refund_provider_reference_missing",
    "cross_organization_provider_reference",
    "provider_error",
];
export function organizationCommerceConfigDocumentPath(organizationId) {
    return `organizations/${organizationId}/config/commerce`;
}
export function organizationCommerceProviderEventDocumentPath(organizationId, eventId) {
    return `organizations/${organizationId}/commerceProviderEvents/${eventId}`;
}
export function organizationCommerceAuditEventDocumentPath(organizationId, eventId) {
    return `organizations/${organizationId}/commerceAuditEvents/${eventId}`;
}
export function commerceConnectedAccountIndexDocumentPath(connectedAccountId) {
    return `commerceConnectedAccounts/${connectedAccountId}`;
}
export function organizationTransactionDocumentPath(organizationId, transactionId) {
    return `organizations/${organizationId}/transactions/${transactionId}`;
}
export function organizationDonationIdempotencyDocumentPath(organizationId, idempotencyDocumentId) {
    return `organizations/${organizationId}/donationIdempotencyKeys/${idempotencyDocumentId}`;
}
export function organizationRefundDocumentPath(organizationId, refundId) {
    return `organizations/${organizationId}/refunds/${refundId}`;
}
export function organizationRefundIdempotencyDocumentPath(organizationId, idempotencyDocumentId) {
    return `organizations/${organizationId}/refundIdempotencyKeys/${idempotencyDocumentId}`;
}
export function organizationLedgerDocumentPath(organizationId, ledgerEntryId) {
    return `organizations/${organizationId}/ledger/${ledgerEntryId}`;
}
export function organizationReceiptDocumentPath(organizationId, receiptId) {
    return `organizations/${organizationId}/receipts/${receiptId}`;
}
export function organizationGivingFundDocumentPath(organizationId, fundId) {
    return `organizations/${organizationId}/givingFunds/${fundId}`;
}
export function organizationCampaignDocumentPath(organizationId, campaignId) {
    return `organizations/${organizationId}/campaigns/${campaignId}`;
}
export function organizationReconciliationRunDocumentPath(organizationId, runId) {
    return `organizations/${organizationId}/reconciliationRuns/${runId}`;
}
export function organizationReconciliationMismatchDocumentPath(organizationId, runId, mismatchId) {
    return `organizations/${organizationId}/reconciliationRuns/${runId}/mismatches/${mismatchId}`;
}
export function donationTransactionHasValidMinorUnitInvariant(transaction) {
    return (transaction.grossAmountMinor === transaction.organizationNetMinor + transaction.platformFeeMinor);
}
export function isGivingFundType(value) {
    return typeof value === "string" && givingFundTypes.includes(value);
}
export function isGivingFundStatus(value) {
    return typeof value === "string" && givingFundStatuses.includes(value);
}
export function isCampaignStatus(value) {
    return typeof value === "string" && campaignStatuses.includes(value);
}
export function isDonationDestinationType(value) {
    return (typeof value === "string" && donationDestinationTypes.includes(value));
}
export function isDonationRefundStatus(value) {
    return (typeof value === "string" && donationRefundStatuses.includes(value));
}
export function isDonationRefundSummaryStatus(value) {
    return (typeof value === "string" &&
        donationRefundSummaryStatuses.includes(value));
}
export function isCommerceReconciliationStatus(value) {
    return (typeof value === "string" &&
        commerceReconciliationStatuses.includes(value));
}
export function computeDonationRefundBalance(input) {
    const refundedAmountMinor = Math.max(input.refundedAmountMinor, 0);
    const refundPendingAmountMinor = Math.max(input.refundPendingAmountMinor, 0);
    const protectedRefundAmountMinor = refundedAmountMinor + refundPendingAmountMinor;
    const remainingRefundableAmountMinor = Math.max(input.amountMinor - protectedRefundAmountMinor, 0);
    let refundStatus = "none";
    if (refundPendingAmountMinor > 0) {
        refundStatus = "processing";
    }
    else if (refundedAmountMinor >= input.amountMinor && input.amountMinor > 0) {
        refundStatus = "refunded";
    }
    else if (refundedAmountMinor > 0) {
        refundStatus = "partiallyRefunded";
    }
    return {
        protectedRefundAmountMinor,
        remainingRefundableAmountMinor,
        refundStatus,
    };
}
export function isValidCampaignGoalAmountMinor(value) {
    return (value === null ||
        (typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= 100_000_000));
}
export function computeGivingDestinationProgress(input) {
    if (input.goalAmountMinor === null || input.goalAmountMinor <= 0) {
        return {
            percentComplete: null,
            remainingAmountMinor: null,
        };
    }
    return {
        percentComplete: Math.floor((input.confirmedAmountMinor * 100) / input.goalAmountMinor),
        remainingAmountMinor: Math.max(input.goalAmountMinor - input.confirmedAmountMinor, 0),
    };
}
export function normalizeStripeConnectedAccountState(account) {
    if (!account) {
        return {
            capabilityStatus: {
                cardPayments: "unknown",
                transfers: "unknown",
            },
            chargesEnabled: false,
            detailsSubmitted: false,
            onboardingStatus: "notStarted",
            payoutsEnabled: false,
            providerCountry: null,
            providerDefaultCurrency: null,
            readiness: "notConnected",
            requirements: emptyCommerceRequirementsSummary(),
            restrictedReasonCode: null,
        };
    }
    const requirements = {
        currentlyDue: safeStringArray(account.requirements?.currently_due),
        disabledReason: safeOptionalString(account.requirements?.disabled_reason),
        eventuallyDue: safeStringArray(account.requirements?.eventually_due),
        pastDue: safeStringArray(account.requirements?.past_due),
        pendingVerification: safeStringArray(account.requirements?.pending_verification),
    };
    const capabilityStatus = {
        cardPayments: normalizeCapabilityStatus(account.capabilities?.card_payments),
        transfers: normalizeCapabilityStatus(account.capabilities?.transfers),
    };
    const chargesEnabled = account.charges_enabled === true;
    const payoutsEnabled = account.payouts_enabled === true;
    const detailsSubmitted = account.details_submitted === true;
    const readiness = commerceReadinessFromProviderState({
        capabilityStatus,
        chargesEnabled,
        detailsSubmitted,
        payoutsEnabled,
        requirements,
    });
    return {
        capabilityStatus,
        chargesEnabled,
        detailsSubmitted,
        onboardingStatus: onboardingStatusFromReadiness(readiness, detailsSubmitted),
        payoutsEnabled,
        providerCountry: safeOptionalString(account.country),
        providerDefaultCurrency: safeOptionalString(account.default_currency),
        readiness,
        requirements,
        restrictedReasonCode: requirements.disabledReason,
    };
}
export function emptyCommerceRequirementsSummary() {
    return {
        currentlyDue: [],
        disabledReason: null,
        eventuallyDue: [],
        pastDue: [],
        pendingVerification: [],
    };
}
function commerceReadinessFromProviderState(input) {
    if (input.requirements.disabledReason || input.requirements.pastDue.length > 0) {
        return "restricted";
    }
    if (!input.detailsSubmitted || input.requirements.currentlyDue.length > 0) {
        return "onboardingRequired";
    }
    if (input.chargesEnabled &&
        input.payoutsEnabled &&
        input.capabilityStatus.cardPayments === "active" &&
        input.capabilityStatus.transfers === "active") {
        return "ready";
    }
    return "pending";
}
function onboardingStatusFromReadiness(readiness, detailsSubmitted) {
    switch (readiness) {
        case "notConnected":
            return "notStarted";
        case "onboardingRequired":
            return detailsSubmitted ? "started" : "started";
        case "pending":
            return "detailsSubmitted";
        case "restricted":
            return "restricted";
        case "ready":
            return "ready";
    }
}
function normalizeCapabilityStatus(value) {
    if (value === "active" || value === "inactive" || value === "pending") {
        return value;
    }
    return "unknown";
}
function safeStringArray(value) {
    return Array.isArray(value)
        ? value.filter((entry) => typeof entry === "string")
        : [];
}
function safeOptionalString(value) {
    return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}
